import { Client, Room } from "colyseus.js";
import { getWsBaseUrl } from "../apiBase";

// Tipos do Schema (espelhados do servidor)
export interface PlayerState {
  id: string;
  sessionId: string;
  name: string;
  x: number;
  y: number;
  z: number;
  rotation: number;
  turretRotation: number;
  health: number;
  maxHealth: number;
  color: string;
  isAlive: boolean;
  isReady: boolean;
  kills: number;
  deaths: number;
  damageDealt: number;
  activeEffect: string;
  effectExpiresAt: number;
  heldItem: string;
  shieldHits: number;
  frozenUntil: number;
  decoyX: number;
  decoyZ: number;
  decoyExpiresAt: number;
  isBot: boolean;
  skinId: string;
  // Efeito cosmético do tanque (aura/rastro) visível para outros jogadores
  effectId: string;
  // [SPECIALS] Poder de linhagem resolvido pelo servidor a partir do tankId
  tankId: string;
  special: string;
  specialPower: number;
  stealthed: boolean;
  // [BESTIÁRIO] Em chamas pelo sopro do Dragão até este timestamp (0 = não)
  burningUntil: number;
}

export interface BulletState {
  id: string;
  ownerId: string;
  x: number;
  y: number;
  z: number;
  dirX: number;
  dirY: number;
  dirZ: number;
  speed: number;
  damage: number;
  size: number;
  createdAt: number;
  cosmeticEffectId?: string;
  cosmeticEffectType?: string;
  // [SPECIALS] Poder de linhagem carimbado no projétil pelo servidor
  special?: string;
  specialPower?: number;
}

export interface ItemState {
  id: string;
  type: string;
  x: number;
  y: number;
  z: number;
  createdAt: number;
  expiresAt: number;
}

export interface GameRulesState {
  gameMode: string;
  killsToWin: number;
  timeLimit: number;
  livesPerPlayer: number;
  maxPlayers: number;
  allowSpectators: boolean;
  damageMultiplier: number;
  speedMultiplier: number;
  healthMultiplier: number;
  tankSpecialsEnabled: boolean;
  enablePowerups: boolean;
  enabledItems: string; // Itens permitidos (separados por vírgula)
  respawnTime: number;
  respawnInvulnerability: number;
  friendlyFire: boolean;
  // Bot settings
  botCount: number;
  botDifficulty: string;
  autoFillBots: boolean;
  removeBotsOnJoin: boolean;
  skillBasedBots: boolean;
}

export interface RocketanksStateType {
  players: Map<string, PlayerState>;
  bullets: BulletState[];
  items: ItemState[];
  rules: GameRulesState;
  status: "waiting" | "countdown" | "playing" | "paused" | "finished";
  roomCode: string;
  hostId: string;
  maxPlayers: number;
  gameSeed: number;
  terrain: string;
  arenaSize: number;
  gameStartTime: number;
  countdownTime: number;
  gameTimeRemaining: number;
  pausedAt: number;
  pausedBy: string;
  winnerId: string;
  winnerName: string;
  endReason: string;
  // [BR] Parâmetros da zona do Battle Royale (sincronizados só nas transições)
  brZoneActive: boolean;
  brZoneFromSize: number;
  brZoneToSize: number;
  brZoneShrinkStartAt: number;
  brZoneShrinkEndAt: number;
  brNextShrinkAt: number;
  brZonePhase: number;
}

export interface GameRulesOptions {
  gameMode?: "elimination" | "deathmatch" | "timed" | "lives" | "battleRoyale";
  killsToWin?: number;
  timeLimit?: number;
  livesPerPlayer?: number;
  maxPlayers?: number;
  damageMultiplier?: number;
  speedMultiplier?: number;
  healthMultiplier?: number;
  respawnTime?: number;
  enabledItems?: string; // Itens permitidos (separados por vírgula)
  // [SPECIALS] Poderes de linhagem dos tanques (host pode desligar)
  tankSpecialsEnabled?: boolean;
  // Bot settings
  botCount?: number;
  botDifficulty?: string;
  autoFillBots?: boolean;
  removeBotsOnJoin?: boolean;
  skillBasedBots?: boolean;
}

let client: Client | null = null;
let currentRoom: Room<RocketanksStateType> | null = null;
let isJoining = false; // Guard contra join concorrente

// Tempo máximo de espera para operações de matchmaking (ms)
// Redes móveis podem ter latência alta - usar tempo generoso
const MATCHMAKING_TIMEOUT_MS = 20000; // 20 segundos

/**
 * Envolve uma Promise com timeout
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timerId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timerId));
}

// Reconnection token storage (Colyseus SDK proper reconnect)
const RECONNECT_TOKEN_KEY = "rk_colyseus_reconnect_token";
const RECONNECT_ROOM_ID_KEY = "rk_colyseus_reconnect_room";

function saveReconnectToken(roomId: string, token: string) {
  try {
    sessionStorage.setItem(RECONNECT_TOKEN_KEY, token);
    sessionStorage.setItem(RECONNECT_ROOM_ID_KEY, roomId);
  } catch { /* ignore */ }
}

function getReconnectToken(): { roomId: string; token: string } | null {
  try {
    const token = sessionStorage.getItem(RECONNECT_TOKEN_KEY);
    const roomId = sessionStorage.getItem(RECONNECT_ROOM_ID_KEY);
    if (token && roomId) return { roomId, token };
  } catch { /* ignore */ }
  return null;
}

export function clearReconnectToken() {
  try {
    sessionStorage.removeItem(RECONNECT_TOKEN_KEY);
    sessionStorage.removeItem(RECONNECT_ROOM_ID_KEY);
  } catch { /* ignore */ }
}

/**
 * Obtém ou cria cliente Colyseus
 */
export function getColyseusClient(serverUrl?: string): Client {
  if (!client) {
    const url = serverUrl || getServerUrl();
    client = new Client(url);
    console.log("[Colyseus Client] Created client for:", url);
  }
  return client;
}

/**
 * Determina URL do servidor baseado no ambiente
 * Usa getWsBaseUrl() para suporte a web e Capacitor (mobile)
 */
function getServerUrl(): string {
  if (typeof window !== "undefined") {
    return getWsBaseUrl();
  }
  // Fallback para desenvolvimento
  return "ws://localhost:4000";
}

/**
 * Obtém a sala atual
 */
export function getCurrentRoom(): Room<RocketanksStateType> | null {
  return currentRoom;
}

/**
 * Cria uma nova sala (com guard contra join concorrente)
 */
export async function createRoom(
  playerName: string,
  terrain: string = "random",
  rules?: GameRulesOptions,
  skinId?: string,
  tankId?: string,
  effectId?: string
): Promise<Room<RocketanksStateType>> {
  if (isJoining) throw new Error("Join already in progress");
  isJoining = true;

  try {
    // Guard: sair da sala anterior antes de entrar numa nova
    await safeLeaveCurrentRoom();

    const colyseusClient = getColyseusClient();
    currentRoom = await withTimeout(
      colyseusClient.create<RocketanksStateType>("rocketanks", {
        playerName,
        terrain,
        rules,
        skinId,
        tankId,
        effectId,
      }),
      MATCHMAKING_TIMEOUT_MS,
      "Timeout ao criar sala - verifique sua conexão com a internet"
    );

    // Salvar reconnectionToken para reconexão nativa do Colyseus
    if (currentRoom.reconnectionToken) {
      saveReconnectToken(currentRoom.id, currentRoom.reconnectionToken);
    }

    console.log("[Colyseus Client] Room created:", currentRoom.id);
    return currentRoom;
  } catch (error) {
    console.error("[Colyseus Client] Failed to create room:", error);
    throw error;
  } finally {
    isJoining = false;
  }
}

/**
 * Entra em uma sala existente por código (com guard contra join concorrente)
 */
export async function joinRoom(
  roomCode: string,
  playerName: string,
  skinId?: string,
  tankId?: string,
  effectId?: string
): Promise<Room<RocketanksStateType>> {
  if (isJoining) throw new Error("Join already in progress");
  isJoining = true;

  try {
    // Guard: sair da sala anterior antes de entrar numa nova
    await safeLeaveCurrentRoom();

    const colyseusClient = getColyseusClient();
    // Tentar entrar por room ID (que é o código)
    currentRoom = await withTimeout(
      colyseusClient.joinById<RocketanksStateType>(roomCode, {
        playerName,
        skinId,
        tankId,
        effectId,
      }),
      MATCHMAKING_TIMEOUT_MS,
      "Timeout ao entrar na sala - verifique o código e sua conexão"
    );
    
    // Salvar reconnectionToken para reconexão nativa do Colyseus
    if (currentRoom.reconnectionToken) {
      saveReconnectToken(currentRoom.id, currentRoom.reconnectionToken);
    }

    console.log("[Colyseus Client] Joined room:", currentRoom.id);
    return currentRoom;
  } catch (error) {
    console.error("[Colyseus Client] Failed to join room:", error);
    throw error;
  } finally {
    isJoining = false;
  }
}

/**
 * Entra ou cria sala (matchmaking automático, com guard)
 */
export async function joinOrCreate(
  playerName: string,
  terrain: string = "random"
): Promise<Room<RocketanksStateType>> {
  if (isJoining) throw new Error("Join already in progress");
  isJoining = true;

  try {
    await safeLeaveCurrentRoom();

    const colyseusClient = getColyseusClient();
    currentRoom = await colyseusClient.joinOrCreate<RocketanksStateType>("rocketanks", {
      playerName,
      terrain,
    });
    
    if (currentRoom.reconnectionToken) {
      saveReconnectToken(currentRoom.id, currentRoom.reconnectionToken);
    }

    console.log("[Colyseus Client] Joined/Created room:", currentRoom.id);
    return currentRoom;
  } catch (error) {
    console.error("[Colyseus Client] Failed to join/create room:", error);
    throw error;
  } finally {
    isJoining = false;
  }
}

/**
 * Helper interno: sair da sala atual de forma segura (idempotente)
 */
async function safeLeaveCurrentRoom(): Promise<void> {
  if (currentRoom) {
    try {
      await currentRoom.leave(true); // consented = true
    } catch (e) {
      console.warn("[Colyseus Client] Error leaving previous room:", e);
    }
    currentRoom = null;
  }
}

/**
 * Sai da sala atual
 */
export async function leaveRoom(): Promise<void> {
  clearReconnectToken();
  await safeLeaveCurrentRoom();
  console.log("[Colyseus Client] Left room");
}

/**
 * Desconecta completamente
 */
export function disconnect(): void {
  clearReconnectToken();
  if (currentRoom) {
    try { currentRoom.leave(true); } catch { /* ignore */ }
    currentRoom = null;
  }
  client = null;
  console.log("[Colyseus Client] Disconnected");
}

/**
 * Reconecta usando reconnectionToken nativo do Colyseus
 * Preserva o sessionId/assento do jogador no servidor
 */
export async function reconnectRoom(): Promise<Room<RocketanksStateType> | null> {
  const saved = getReconnectToken();
  if (!saved) {
    console.log("[Colyseus Client] No reconnection token available");
    return null;
  }

  if (isJoining) return null;
  isJoining = true;

  try {
    await safeLeaveCurrentRoom();
    const colyseusClient = getColyseusClient();
    currentRoom = await colyseusClient.reconnect<RocketanksStateType>(saved.token);

    // Atualizar token (pode mudar após reconnect)
    if (currentRoom.reconnectionToken) {
      saveReconnectToken(currentRoom.id, currentRoom.reconnectionToken);
    }

    console.log("[Colyseus Client] Reconnected to room:", currentRoom.id);
    return currentRoom;
  } catch (error) {
    console.warn("[Colyseus Client] Reconnect failed, clearing token:", error);
    clearReconnectToken();
    return null;
  } finally {
    isJoining = false;
  }
}

// Movimento client-authoritative (posição direta)
export const sendMovePosition = (
  position: { x: number; y: number; z: number },
  rotation: number,
  turretRotation: number
) => {
  currentRoom?.send("move", {
    x: position.x,
    y: position.y,
    z: position.z,
    rotation,
    turretRotation,
  });
};

export const sendShoot = (bulletId: string, aimAngle: number) => {
  currentRoom?.send("shoot", { bulletId, aimAngle });
};

/**
 * [SYNC] Envia estado de carregamento do tiro para outros jogadores verem
 * @param isCharging - se está carregando
 * @param chargeLevel - nível de carga 0-1
 */
export const sendChargeUpdate = (isCharging: boolean, chargeLevel: number) => {
  currentRoom?.send("chargeUpdate", { isCharging, chargeLevel });
};

export const sendShootWithPosition = (
  bulletId: string,
  position: { x: number; y: number; z: number },
  direction: { x: number; y: number; z: number },
  metadata?: {
    speed?: number;
    damage?: number;
    size?: number;
    cosmeticEffectId?: string | null;
    cosmeticEffectType?: string | null;
  }
) => {
  // Calcular aimAngle a partir da direção
  const aimAngle = Math.atan2(direction.x, direction.z);
  currentRoom?.send("shoot", { bulletId, aimAngle, position, direction, metadata });
};

/**
 * [NETCODE] Envia um disparo completo (volley) em UMA mensagem:
 * todos os projéteis do tiro (tanques multi-tiro) + nível de carga.
 * O servidor valida cooldown por volley e deriva dano/velocidade/tamanho
 * do chargeLevel — mantendo tiro carregado e multi-tiro sincronizados.
 */
export const sendShootVolley = (
  shots: { id: string; angle: number }[],
  chargeLevel: number,
  metadata?: {
    cosmeticEffectId?: string | null;
    cosmeticEffectType?: string | null;
  }
) => {
  currentRoom?.send("shoot", { shots, chargeLevel, metadata });
};

export const sendStartGame = () => {
  currentRoom?.send("startGame", {});
};

export const sendTogglePause = (paused: boolean) => {
  currentRoom?.send("togglePause", { paused });
};

export const sendChangeColor = (color: string) => {
  currentRoom?.send("changeColor", { color });
};

export const sendAbandon = () => {
  currentRoom?.send("abandon", {});
};

export const sendToggleReady = () => {
  currentRoom?.send("toggleReady", {});
};

export const sendUpdateRules = (rules: GameRulesOptions) => {
  currentRoom?.send("updateRules", rules);
};

export const sendRestartGame = () => {
  currentRoom?.send("restartGame", {});
};

export const sendUseItem = () => {
  currentRoom?.send("useItem", {});
};

export const sendUpdateSkin = (skinId: string) => {
  currentRoom?.send("updateSkin", { skinId });
};

// Efeito cosmético do tanque (aura/rastro) — sincronizado para outros jogadores
export const sendUpdateEffect = (effectId: string) => {
  currentRoom?.send("updateEffect", { effectId });
};

// [SPECIALS] Troca de tanque no lobby — o servidor valida o tankId contra o
// catálogo canônico e recalcula o poder de linhagem.
export const sendUpdateTank = (tankId: string) => {
  currentRoom?.send("updateTank", { tankId });
};
